interface HeatmapCell {
  x: string | number
  y: string | number
  value: number
}

interface HeatmapComponentProps {
  data: Array<Record<string, any>>
  xAxisKey: string
  yAxisKey: string
  valueKey: string
  title?: string
  colorScale?: string[]
  showValues?: boolean
  loading?: boolean
}

// Escala de colores por defecto (verde -> amarillo -> rojo)
const DEFAULT_COLOR_SCALE = [
  '#10b981', // Verde claro
  '#84cc16', // Lima
  '#fbbf24', // Amarillo
  '#f59e0b', // Naranja
  '#ef4444', // Rojo
  '#dc2626', // Rojo oscuro
]

export function HeatmapComponent({
  data,
  xAxisKey,
  yAxisKey,
  valueKey,
  title,
  colorScale = DEFAULT_COLOR_SCALE,
  showValues = true,
  loading = false,
}: HeatmapComponentProps) {
  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center text-gray-500">
          No hay datos disponibles
        </div>
      </div>
    )
  }

  // Transformar datos a formato de heatmap
  const heatmapData: HeatmapCell[] = data.map((item) => ({
    x: item[xAxisKey],
    y: item[yAxisKey],
    value: Number(item[valueKey]) || 0,
  }))

  // Obtener valores únicos para ejes
  const xValues = Array.from(new Set(heatmapData.map((d) => d.x)))
  const yValues = Array.from(new Set(heatmapData.map((d) => d.y)))

  // Calcular min/max para la escala de color
  const values = heatmapData.map((d) => d.value)
  const minValue = Math.min(...values)
  const maxValue = Math.max(...values)

  // Función para obtener color basado en valor
  const getColor = (value: number): string => {
    if (maxValue === minValue) return colorScale[0]

    const ratio = (value - minValue) / (maxValue - minValue)
    const index = Math.min(
      Math.floor(ratio * colorScale.length),
      colorScale.length - 1
    )

    return colorScale[index]
  }

  // Crear matriz de datos
  const dataMatrix = new Map<string, number>()
  heatmapData.forEach((cell) => {
    const key = `${cell.x}-${cell.y}`
    dataMatrix.set(key, cell.value)
  })

  // Función para obtener valor de celda
  const getCellValue = (x: string | number, y: string | number): number | null => {
    const key = `${x}-${y}`
    return dataMatrix.get(key) ?? null
  }

  return (
    <div className="bg-white border rounded-lg p-6">
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}

      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          {/* Tabla heatmap */}
          <table className="border-collapse">
            <thead>
              <tr>
                <th className="border border-gray-300 bg-gray-100 p-2 text-xs font-semibold sticky left-0 z-10">
                  {yAxisKey} \ {xAxisKey}
                </th>
                {xValues.map((x) => (
                  <th
                    key={String(x)}
                    className="border border-gray-300 bg-gray-100 p-2 text-xs font-semibold min-w-[80px]"
                  >
                    {String(x)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {yValues.map((y) => (
                <tr key={String(y)}>
                  <td className="border border-gray-300 bg-gray-100 p-2 text-xs font-semibold sticky left-0 z-10">
                    {String(y)}
                  </td>
                  {xValues.map((x) => {
                    const value = getCellValue(x, y)
                    const color = value !== null ? getColor(value) : '#f3f4f6'

                    return (
                      <td
                        key={`${x}-${y}`}
                        className="border border-gray-300 p-2 text-center transition-all hover:opacity-80 cursor-pointer"
                        style={{ backgroundColor: color }}
                        title={value !== null ? `${x} - ${y}: ${value.toLocaleString('es-ES')}` : 'Sin datos'}
                      >
                        {showValues && value !== null && (
                          <span className="text-xs font-medium text-white drop-shadow">
                            {value.toLocaleString('es-ES', { maximumFractionDigits: 1 })}
                          </span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Leyenda de escala */}
      <div className="mt-4 flex items-center justify-center gap-4 text-xs">
        <span className="text-gray-600">Min: {minValue.toLocaleString('es-ES')}</span>
        <div className="flex gap-1">
          {colorScale.map((color, index) => (
            <div
              key={index}
              className="w-10 h-4 rounded"
              style={{ backgroundColor: color }}
            ></div>
          ))}
        </div>
        <span className="text-gray-600">Max: {maxValue.toLocaleString('es-ES')}</span>
      </div>
    </div>
  )
}
